import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkle } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { loginUser, registerUser } from "../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [idConsultor, setIdConsultor] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setNombre("");
    setEmail("");
    setPassword("");
    setIdConsultor("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let data;
      if (mode === "login") {
        data = await loginUser(email, password);
      } else {
        data = await registerUser(nombre, email, password, idConsultor ? parseInt(idConsultor, 10) : null);
      }
      login(data.access_token, data.user);
      navigate("/", { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        (mode === "login"
          ? "Email o contraseña incorrectos."
          : "No se pudo crear la cuenta. Inténtalo de nuevo.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#C57B67] flex items-center justify-center mb-3">
            <Sparkle size={24} weight="fill" className="text-white" />
          </div>
          <h1 className="text-2xl font-semibold text-[#2D2A26] font-display">Hesttya</h1>
          <p className="text-sm text-[#736F68] mt-1">Panel de seguimiento de equipo</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl border border-[#E5E0D8] p-8 shadow-sm">
          {/* Tab toggle */}
          <div className="flex rounded-lg border border-[#E5E0D8] p-1 mb-6 gap-1">
            <button
              type="button"
              onClick={() => switchMode("login")}
              className={`flex-1 text-sm font-semibold rounded-md py-1.5 transition-colors ${
                mode === "login"
                  ? "bg-[#C57B67] text-white"
                  : "text-[#736F68] hover:text-[#2D2A26]"
              }`}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => switchMode("register")}
              className={`flex-1 text-sm font-semibold rounded-md py-1.5 transition-colors ${
                mode === "register"
                  ? "bg-[#C57B67] text-white"
                  : "text-[#736F68] hover:text-[#2D2A26]"
              }`}
            >
              Crear cuenta
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label
                    htmlFor="nombre"
                    className="block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5"
                  >
                    Nombre completo
                  </label>
                  <input
                    id="nombre"
                    type="text"
                    autoComplete="name"
                    required
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    className={inputCls}
                    placeholder="Tu nombre"
                  />
                </div>
                <div>
                  <label
                    htmlFor="id_consultor"
                    className="block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5"
                  >
                    ID Consultor
                  </label>
                  <input
                    id="id_consultor"
                    type="number"
                    value={idConsultor}
                    onChange={(e) => setIdConsultor(e.target.value)}
                    className={inputCls}
                    placeholder="Número de consultora en Hesttya"
                  />
                  <p className="text-[11px] text-[#B0A99F] mt-1">
                    Lo encuentras en tu perfil de app.hesttya.pt
                  </p>
                </div>
              </>
            )}

            <div>
              <label
                htmlFor="email"
                className="block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="tu@email.com"
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-xs font-semibold uppercase tracking-wider text-[#736F68] mb-1.5"
              >
                Contraseña
              </label>
              <input
                id="password"
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C57B67] hover:bg-[#B06A57] disabled:opacity-60 text-white font-semibold text-sm rounded-lg px-4 py-2.5 transition-colors mt-2"
            >
              {loading
                ? mode === "login" ? "Entrando…" : "Creando cuenta…"
                : mode === "login" ? "Entrar" : "Crear cuenta"}
            </button>
          </form>
        </div>

        {mode === "register" && (
          <p className="text-center text-xs text-[#736F68] mt-4">
            La cuenta se crea como Coordinador de Área.
          </p>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full border border-[#E5E0D8] rounded-lg px-3 py-2.5 text-sm text-[#2D2A26] bg-white placeholder-[#B0A99F] focus:outline-none focus:ring-2 focus:ring-[#C57B67] focus:border-transparent transition";
